import { useEffect, useState } from 'react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import {
  AccountData,
  GrowRegistrationStatus,
  GrowRegistrationStatusOut,
  HolidayCalendar,
  PaymentIntegrationType,
  StorefrontStatus,
} from '../api';
import { useGetAccountAuthenticated } from '../session/swr/useGetAccountAuthenticated';
import { UseAuthPermissions } from '../session/UseAuthPermissions';
import { useIsGrowAccount } from '../common/payments/useIsGrowAccount';
import { useGrowRegistrationStatus } from './swr/useGrowRegistrationStatus';
import { GROW_CONNECT_FEATURE_FLAG } from './growConnectUtils';

export enum GrowConnectStateType {
  LOADING = 'loading',
  INELIGIBLE = 'ineligible',
  NOT_ADMIN = 'not_admin',
  STATUS_UNAVAILABLE = 'status_unavailable',
  CONNECTED = 'connected',
  APPROVED_FINALISING = 'approved_finalising',
  APPROVED_NOT_CONNECTED = 'approved_not_connected',
  PENDING = 'pending',
  BLOCKED_NO_STOREFRONT = 'blocked_no_storefront',
  FORM = 'form',
}

export type GrowConnectState = {
  type: GrowConnectStateType;
  account?: AccountData;
  registration?: GrowRegistrationStatusOut;
  /** The provider currently configured, when it is something other than Grow. */
  existingProvider?: PaymentIntegrationType;
  refetchRegistration: () => Promise<unknown>;
  refetchAccount: () => Promise<unknown>;
};

const hasPublishableStorefront = (account?: AccountData): boolean =>
  !!account?.storefronts?.some(
    (storefront) => storefront.status === StorefrontStatus.ACTIVE && !!storefront.short_code,
  );

const getExistingProvider = (account?: AccountData): PaymentIntegrationType | undefined => {
  const provider = account?.payment_integration_settings?.payment_integration_type;
  if (
    !provider ||
    provider === PaymentIntegrationType.NO_INTEGRATION ||
    provider === PaymentIntegrationType.GROW_PAYMENTS
  ) {
    return undefined;
  }
  return provider;
};

export const useGrowConnectState = (): GrowConnectState => {
  const { isAdmin } = UseAuthPermissions();
  const isFeatureEnabled = useFeatureFlagEnabled(GROW_CONNECT_FEATURE_FLAG);
  const { account, isLoading: isAccountLoading, updateLocalAccount } = useGetAccountAuthenticated({});
  const isGrowAccount = useIsGrowAccount();

  const isIsraeliAccount = account?.holiday_calendar === HolidayCalendar.ISR;
  const canRegister = isAdmin && !!isFeatureEnabled && isIsraeliAccount;

  const {
    registration,
    error: statusError,
    isLoading: isStatusLoading,
    refetchRegistration,
  } = useGrowRegistrationStatus({ shouldNotMakeRequest: !canRegister });

  // The account is cached with revalidateOnFocus disabled, so at the moment the status flips to
  // approved it still reports the old provider. Refetch once, and only trust "approved but not
  // connected" after that refetch has come back -- otherwise every real approval would flash a
  // "payments are not running through Grow" warning on its way to the success state.
  // State rather than a ref on purpose: a refetch that returns an unchanged account would not
  // re-render on its own, and the finalising state would spin forever.
  const [approvalRefetched, setApprovalRefetched] = useState(false);
  const isApproved = registration?.status === GrowRegistrationStatus.APPROVED;
  useEffect(() => {
    if (isApproved && !isGrowAccount && !approvalRefetched) {
      void updateLocalAccount().finally(() => setApprovalRefetched(true));
    }
  }, [isApproved, isGrowAccount, approvalRefetched]);

  const base = {
    account,
    registration,
    existingProvider: getExistingProvider(account),
    refetchRegistration: async () => refetchRegistration(),
    refetchAccount: async () => updateLocalAccount(),
  };

  if (!isAdmin) {
    return { ...base, type: GrowConnectStateType.NOT_ADMIN };
  }
  if (isAccountLoading || !account || isFeatureEnabled === undefined) {
    return { ...base, type: GrowConnectStateType.LOADING };
  }
  if (!isIsraeliAccount || !isFeatureEnabled) {
    return { ...base, type: GrowConnectStateType.INELIGIBLE };
  }
  if (isStatusLoading) {
    return { ...base, type: GrowConnectStateType.LOADING };
  }
  // Without a status we cannot tell an open lead from no lead at all. Showing the form here would
  // let a merchant register a second time on top of one already in flight.
  if (statusError) {
    return { ...base, type: GrowConnectStateType.STATUS_UNAVAILABLE };
  }
  // The status key only becomes live once the account confirms eligibility, so there is a render
  // where the request has not settled yet. That is still loading, not a failure.
  if (!registration) {
    return { ...base, type: GrowConnectStateType.LOADING };
  }
  if (isApproved && isGrowAccount) {
    return { ...base, type: GrowConnectStateType.CONNECTED };
  }
  if (isApproved) {
    return {
      ...base,
      type: approvalRefetched ? GrowConnectStateType.APPROVED_NOT_CONNECTED : GrowConnectStateType.APPROVED_FINALISING,
    };
  }
  if (registration.status === GrowRegistrationStatus.PENDING) {
    return { ...base, type: GrowConnectStateType.PENDING };
  }
  if (isGrowAccount) {
    return { ...base, type: GrowConnectStateType.CONNECTED };
  }
  // Grow reviews a real, live page for the business before approving, and we send them the
  // account's storefront short link as that page. The server enforces this on every register call,
  // so gate the form rather than the status.
  if (!hasPublishableStorefront(account)) {
    return { ...base, type: GrowConnectStateType.BLOCKED_NO_STOREFRONT };
  }
  return { ...base, type: GrowConnectStateType.FORM };
};
